import { useState, useCallback, useRef } from 'react';

export const STAGES = {
  IDLE: 'idle',
  SCRAPING: 'scraping',
  EXTRACTING: 'extracting',
  EXPANDING: 'expanding',
  RANKING: 'ranking',
  COMPLETE: 'complete',
  ERROR: 'error',
};

const STAGE_ORDER = ['scraping', 'extracting', 'expanding', 'ranking', 'complete'];

export function useLookup() {
  const [stage, setStage] = useState(STAGES.IDLE);
  const [message, setMessage] = useState('');
  const [product, setProduct] = useState(null);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState(null);
  const [liveKeywords, setLiveKeywords] = useState([]);
  const abortRef = useRef(null);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setStage(STAGES.IDLE);
    setMessage('');
    setProduct(null);
    setResults([]);
    setProgress({ done: 0, total: 0 });
    setError(null);
    setLiveKeywords([]);
  }, []);

  const lookup = useCallback(async (asin, marketplace) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStage(STAGES.SCRAPING);
    setMessage('Starting lookup...');
    setProduct(null);
    setResults([]);
    setLiveKeywords([]);
    setError(null);
    setProgress({ done: 0, total: 0 });

    try {
      const res = await fetch('/api/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asin, marketplace }),
        signal: controller.signal,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = data?.detail || 'Something went wrong. Please try again.';
        setError(msg);
        setStage(STAGES.ERROR);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            handleEvent(event);
          } catch {
            // ignore malformed line
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setError('Connection lost. Please check your internet and try again.');
      setStage(STAGES.ERROR);
    }
  }, []);

  function handleEvent(event) {
    switch (event.stage) {
      case 'scraping':
        setStage(STAGES.SCRAPING);
        setMessage(event.message);
        break;
      case 'product':
        setProduct(event.data);
        break;
      case 'extracting':
        setStage(STAGES.EXTRACTING);
        setMessage(event.message);
        break;
      case 'seeds':
        setMessage(`Found ${event.count} seed keywords`);
        break;
      case 'expanding':
        setStage(STAGES.EXPANDING);
        setMessage(event.message);
        break;
      case 'expanded':
        setMessage(`Expanded to ${event.count} total keyword candidates`);
        break;
      case 'ranking':
        setStage(STAGES.RANKING);
        setMessage(event.message);
        break;
      case 'progress':
        setProgress({ done: event.done, total: event.total });
        break;
      case 'rank_found':
        setLiveKeywords(prev => [event.keyword, ...prev].slice(0, 100));
        setResults(prev => {
          const updated = [...prev, event.keyword].sort((a, b) => a.rank - b.rank);
          return updated;
        });
        break;
      case 'complete':
        setResults(event.results);
        setStage(STAGES.COMPLETE);
        setMessage(`Found ${event.found} ranking keywords out of ${event.total_checked} checked`);
        break;
      case 'error':
        setError(event.message);
        setStage(STAGES.ERROR);
        break;
      default:
        break;
    }
  }

  const progressPct = progress.total > 0
    ? Math.round((progress.done / progress.total) * 100)
    : stageProgress(stage);

  return { stage, message, product, results, liveKeywords, progress, progressPct, error, lookup, reset };
}

function stageProgress(stage) {
  const map = {
    [STAGES.SCRAPING]: 10,
    [STAGES.EXTRACTING]: 30,
    [STAGES.EXPANDING]: 50,
    [STAGES.RANKING]: 60,
    [STAGES.COMPLETE]: 100,
  };
  return map[stage] ?? 0;
}
